import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { Warning as WarningIcon, Refresh as RefreshIcon } from '@mui/icons-material';

export interface ErrorStateProps {
  /**
   * Main error title
   * @default "Something went wrong"
   */
  title?: string;
  /**
   * Descriptive error message
   * @default "An error occurred while loading data. Please try again."
   */
  message?: string;
  /**
   * Callback when retry button is clicked
   * If not provided, retry button won't be shown
   */
  onRetry?: () => void;
  /**
   * Text for the retry button
   * @default "Try Again"
   */
  retryText?: string;
  /**
   * Whether to show a Paper wrapper
   * @default false
   */
  contained?: boolean;
  /**
   * Custom icon to display
   * @default WarningIcon
   */
  icon?: React.ReactNode;
  /**
   * Size variant
   * @default "medium"
   */
  size?: 'small' | 'medium' | 'large';
}

const sizeConfig = {
  small: { iconSize: 32, titleVariant: 'subtitle1' as const, spacing: 1 },
  medium: { iconSize: 48, titleVariant: 'h6' as const, spacing: 2 },
  large: { iconSize: 64, titleVariant: 'h5' as const, spacing: 3 },
};

/**
 * Reusable error state component for displaying inline errors.
 * Use this when data loading fails or an operation encounters an error.
 *
 * Note: This is different from ErrorBoundary which catches React errors.
 * ErrorState is for displaying known error states in the UI.
 *
 * @example
 * // Basic usage
 * <ErrorState
 *   title="Failed to load exporters"
 *   message="Network error - please check your connection"
 *   onRetry={() => refetch()}
 * />
 *
 * @example
 * // Contained in a Paper
 * <ErrorState
 *   title="No data available"
 *   message="Unable to fetch metrics"
 *   onRetry={handleRetry}
 *   contained
 * />
 *
 * @example
 * // Small size for inline use
 * <ErrorState
 *   message="Failed to load"
 *   onRetry={retry}
 *   size="small"
 * />
 */
export function ErrorState({
  title = 'Something went wrong',
  message = 'An error occurred while loading data. Please try again.',
  onRetry,
  retryText = 'Try Again',
  contained = false,
  icon,
  size = 'medium',
}: ErrorStateProps) {
  const config = sizeConfig[size];

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: config.spacing * 2,
        px: config.spacing,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: config.iconSize + 16,
          height: config.iconSize + 16,
          borderRadius: 2,
          bgcolor: 'warning.light',
          mb: config.spacing,
        }}
      >
        {icon || (
          <WarningIcon
            sx={{
              fontSize: config.iconSize,
              color: 'warning.dark',
            }}
          />
        )}
      </Box>

      <Typography
        variant={config.titleVariant}
        fontWeight="bold"
        gutterBottom
        color="text.primary"
      >
        {title}
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: onRetry ? config.spacing : 0, maxWidth: 400 }}
      >
        {message}
      </Typography>

      {onRetry && (
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={onRetry}
          size={size === 'small' ? 'small' : 'medium'}
          sx={{ borderRadius: '999px' }}
        >
          {retryText}
        </Button>
      )}
    </Box>
  );

  if (contained) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        {content}
      </Paper>
    );
  }

  return content;
}

export default ErrorState;
